import uniq from 'lodash/uniq';
import * as React from 'react';
import { noop } from 'vet-bones/utils';

import * as copy from 'src/templates/copy';
import {
  INVALID_IRI_MATCHER,
  IRI_STARTS_WITH_MATCHER,
} from 'src/utils/removeInvalidIriChars';

const validateName = (name: string, _index: number) =>
  name ? '' : copy.components.projectSettingsDrawer.validation.nameRequired;

const validateIri = (iri: string, _index: number): string => {
  if (!iri) {
    return copy.components.projectSettingsDrawer.validation.namespaceRequired;
  }

  if (!iri.match(IRI_STARTS_WITH_MATCHER)) {
    return copy.components.projectSettingsDrawer.validation.namespaceStartsWith;
  }

  if (!iri.includes(':')) {
    return copy.components.projectSettingsDrawer.validation.namespaceSemicolon;
  }

  const invalidChars = iri.match(INVALID_IRI_MATCHER);
  if (invalidChars) {
    return copy.components.projectSettingsDrawer.validation.namespaceChars(
      uniq(invalidChars)
    );
  }

  return '';
};

type SettingsValidationKey =
  | 'name'
  | 'namespace'
  | 'modelPrefixes'
  | 'modelNamespaces';

export type ProjectSettingsValidationValue = {
  validation: {
    name: boolean;
    namespace: boolean;
    modelNamespaces: Record<string, boolean>;
    modelPrefixes: Record<string, boolean>;
  };
  withValidation: (
    name: SettingsValidationKey,
    index?: any
  ) => (value: string) => string;
};

export const ProjectSettingsValidationContext =
  React.createContext<ProjectSettingsValidationValue>({
    validation: {
      name: true,
      namespace: true,
      modelNamespaces: {},
      modelPrefixes: {},
    },
    withValidation: noop as any,
  });

export const useProjectSettingsValidation = () => {
  const contextMenu = React.useContext(ProjectSettingsValidationContext);
  return contextMenu;
};

export const useProjectSettingsValidationValue = ({
  sortedModelIds,
  modelNamespaces,
  modelPrefixes,
  shouldPublishPrefixByModel,
}: {
  sortedModelIds: string[];
  modelNamespaces: Record<string, string>;
  modelPrefixes: Record<string, string>;
  shouldPublishPrefixByModel: Record<string, boolean>;
}) => {
  const validatePrefix = React.useCallback(
    (prefix: string, index: number): string => {
      const currentModelId = sortedModelIds[index];
      if (!currentModelId) {
        return '';
      }

      const shouldPublishCurrent = shouldPublishPrefixByModel[currentModelId];

      if (shouldPublishCurrent === false) {
        return '';
      }

      if (!prefix) {
        return copy.components.projectSettingsDrawer.validation.prefixRequired;
      }

      const currentNamespace = modelNamespaces[currentModelId];
      const isPrefixDuped = sortedModelIds.some(
        (modelId, innerIndex: number) => {
          if (innerIndex === index) {
            return false;
          }

          const modelPrefix = modelPrefixes[modelId];
          const modelNamespace = modelNamespaces[modelId];
          const modelShouldPublish = shouldPublishPrefixByModel[modelId];
          if (
            modelShouldPublish &&
            prefix === modelPrefix &&
            currentNamespace !== modelNamespace
          ) {
            return true;
          }

          return false;
        }
      );

      if (isPrefixDuped) {
        return copy.components.projectSettingsDrawer.validation
          .prefixAlreadyExists;
      }

      return '';
    },
    [modelNamespaces, modelPrefixes, shouldPublishPrefixByModel, sortedModelIds]
  );

  const [validation, setValidation] = React.useState({
    name: true,
    namespace: true,
    modelNamespaces: Object.fromEntries(sortedModelIds.map((id) => [id, true])),
    modelPrefixes: Object.fromEntries(sortedModelIds.map((id) => [id, true])),
  });

  const withValidation = React.useCallback(
    (name: keyof typeof validation, index = 0) => {
      return (value: string) => {
        let error = '';
        if (name === 'modelNamespaces' || name === 'modelPrefixes') {
          const handler =
            name === 'modelNamespaces' ? validateIri : validatePrefix;
          error = handler(value, index);
          const isValid = error === '';

          const modelId = sortedModelIds[index];
          if (validation[name][modelId] !== isValid) {
            setValidation((prev) => ({
              ...prev,
              [name]: { ...prev[name], [modelId]: isValid },
            }));
          }
        } else {
          const handler = name === 'name' ? validateName : validateIri;
          error = handler(value, index);
          const isValid = error === '';

          if (validation[name] !== isValid) {
            setValidation((prev) => ({
              ...prev,
              [name]: isValid,
            }));
          }
        }

        return error;
      };
    },
    [sortedModelIds, validatePrefix, validation]
  );

  return {
    validation,
    withValidation,
  };
};
